import type { AxiosInstance } from "axios";

const MAX_RETRIES = 2;
// Transient upstream failures worth replaying. 525 is Cloudflare's
// "origin handshake failed" — it shows up when a burst of parallel requests
// outruns what the origin will accept, and the same request succeeds on retry.
const RETRYABLE_STATUSES = new Set([429, 502, 503, 504, 522, 524, 525]);
// Only replay methods that are safe to repeat. Silently re-sending a POST
// after a 429 could double-create records.
const IDEMPOTENT_METHODS = new Set(["get", "head", "options"]);

/**
 * Retries rate-limited (429) and transient upstream (502/503/504/52x) responses
 * with backoff, honouring the server's Retry-After header when present.
 *
 * Attach this to an axios instance *before* any interceptor that reshapes the
 * error — axios runs response interceptors in registration order, and most of
 * our clients convert AxiosError into a plain `{message, statusCode}` object,
 * which discards `error.response`.
 */
export function attachRateLimitRetry(instance: AxiosInstance) {
  instance.interceptors.response.use(undefined, async (error) => {
    const config = error?.config;
    const response = error?.response;
    const status = response?.status;

    if (
      !config ||
      typeof status !== "number" ||
      !RETRYABLE_STATUSES.has(status) ||
      !IDEMPOTENT_METHODS.has((config.method ?? "get").toLowerCase())
    ) {
      return Promise.reject(error);
    }

    const attempt = (config.__rateLimitRetryCount ?? 0) + 1;
    if (attempt > MAX_RETRIES) return Promise.reject(error);
    config.__rateLimitRetryCount = attempt;

    const retryAfterSeconds = Number(response.headers?.["retry-after"]);
    const baseDelay =
      Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
        ? retryAfterSeconds * 1000
        : Math.min(1000 * 2 ** attempt, 10_000);

    // Jitter so concurrent requests from the same page don't all retry in lockstep.
    await new Promise((resolve) =>
      setTimeout(resolve, baseDelay + Math.random() * 250)
    );

    return instance.request(config);
  });
}
